<template>
  <div class="project my-10 px-2">
    <div class="d-flex justify-space-between" :style="{ position: 'relative' }">
      <!-- left -->
      <div class="d-flex">
        <!-- new project -->
        <div class="text-center">
          <span class="overline"> New </span>
          <div class="mb-4">
            <v-btn
              :min-width="laptop ? '40px' : '42px'"
              height="38px"
              class="elevation-2 px-0"
              color="secondary"
              :loading="dropdownsLoading"
              @click="handleForm({})"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- form -->
        <v-dialog
          max-width="800"
          :value="poModal"
          @click:outside="poModal = false"
          @keydown.esc="poModal = false"
        >
          <ProjectsForm
            :clientsList="clients"
            :statusesList="statuses"
            :typesList="types"
            :checklist="checklistItems"
            :prioritiesList="priorities"
            :staff="staffList"
            :driList="DRIs"
            :itemToEdit="itemToEdit"
            @project-table-refresh="handleRefresh"
            @cancel="handleRefresh"
          />
        </v-dialog>

        <!-- clear filters -->
        <div class="text-center ml-2">
          <span class="overline"> Clear </span>
          <div class="mb-4">
            <v-btn
              :min-width="laptop ? '40px' : '42px'"
              height="38px"
              class="elevation-2 px-0"
              color="secondary"
              @click="clearFilters(false)"
            >
              <v-icon>mdi-filter-remove</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- client filter -->
        <div class="ml-lg-2 ml-md-1 text-center">
          <span class="overline"> Client </span>
          <div class="d-flex mb-4">
            <v-autocomplete
              class="project__main-selector selector"
              :class="{ laptop }"
              item-text="name"
              :items="clients"
              v-model="filterClient"
              solo
              dense
              hide-details
              :loading="dropdownsLoading"
              @input="handleFilterQuery('Client', $event)"
            ></v-autocomplete>
          </div>
        </div>

        <!-- project manager filter -->
        <div class="ml-lg-2 ml-md-1 text-center">
          <span class="overline"> Project Manager </span>
          <div class="d-flex mb-4">
            <v-autocomplete
              class="project__main-selector selector"
              :class="{ laptop }"
              :items="DRIs"
              :item-text="fullName"
              v-model="filterPM"
              solo
              dense
              hide-details
              :loading="dropdownsLoading"
              @input="handleFilterQuery('PM', $event)"
            ></v-autocomplete>
          </div>
        </div>

        <!-- est start date filter -->
        <div class="ml-lg-2 ml-md-1 text-center">
          <span class="overline"> Est Start Date(s) </span>
          <div class="d-flex align-end">
            <v-menu
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              solo-inverted
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filterEstStartDateRange"
                  class="project__date-selector selector"
                  solo
                  dense
                  readonly
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearFilters(true)"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filterEstStartDate"
                range
                @input="handleFilterQuery('EstStartDate', $event)"
              ></v-date-picker>
            </v-menu>
          </div>
        </div>

        <!-- status filter -->
        <div class="ml-lg-2 ml-md-1 text-center">
          <span class="overline"> Status </span>
          <div class="d-flex mb-4">
            <v-autocomplete
              class="project__secondary-selector selector"
              :class="{ laptop }"
              :items="statuses"
              v-model="filterStatus"
              solo
              dense
              hide-details
              :loading="dropdownsLoading"
              @input="handleFilterQuery('Status', $event)"
            ></v-autocomplete>
          </div>
        </div>

        <!-- priority filter -->
        <transition>
          <div v-show="!expandMenu" class="ml-lg-2 ml-md-1 text-center">
            <span class="overline"> Priority </span>
            <div class="d-flex mb-4">
              <v-autocomplete
                class="project__secondary-selector selector"
                :class="{ laptop }"
                :items="priorities"
                v-model="filterPriority"
                solo
                dense
                hide-details
                :loading="dropdownsLoading"
                @input="handleFilterQuery('Priority', $event)"
              ></v-autocomplete>
            </div>
          </div>
        </transition>
      </div>

      <!-- right -->
      <div class="d-flex flex-column justify-start align-end my-0">
        <!-- search -->
        <span class="overline" :style="{ opacity: 0, userSelect: 'none' }"
          >Search</span
        >
        <div class="mb-4">
          <v-badge
            :content="laptopSmaller ? '...' : filterSearchBadge"
            :value="filterSearch && !expandMenu"
            color="secondary"
            overlap
          >
            <v-btn
              height="38px"
              min-width="48px"
              class="elevation-2 px-0"
              outlined
              color="accent"
              @click="expandMenu = !expandMenu"
            >
              <v-icon v-if="expandMenu">mdi-magnify-minus-outline</v-icon>
              <v-icon v-else>mdi-magnify-plus-outline</v-icon>
            </v-btn>
          </v-badge>
          <v-expand-x-transition>
            <v-text-field
              :style="{ position: 'absolute', bottom: '16px', right: '60px' }"
              v-show="expandMenu"
              v-model="filterSearch"
              class="project__search-selector"
              solo
              dense
              hide-details
              clearable
            />
          </v-expand-x-transition>
        </div>
      </div>
    </div>
    <!-- table -->
    <v-card>
      <v-skeleton-loader
        v-if="showTableLoader || dropdownsLoading"
        type="table"
        height="100vh"
      ></v-skeleton-loader>
      <ProjectsTable
        v-if="tableData && !showTableLoader && !dropdownsLoading"
        :tableData="tableData"
        :filterSearch="filterSearch"
        :filterClient="filterClient ? filterClient : ''"
        :filterPM="filterPM ? filterPM : ''"
        :filterEstStartDate="filterEstStartDate"
        :filterStatus="filterStatus ? filterStatus : ''"
        :filterPriority="filterPriority ? filterPriority : ''"
        :checklist="checklistItems"
        @call-edit-form="handleForm"
      />
    </v-card>
  </div>
</template>

<script>
// libraries
import _ from "lodash";
import { mapActions } from "vuex";
// internal
import {
  getEnum,
  getClients,
  getAllStaff,
  getAllProjects,
  getSpecificEnum,
  getCustomerPriceList
} from "@/utils/newDbUtils";
import { customBreakpoint, timeUnits } from "@/utils/mixins";
import { ROUTES } from "@/utils/constants";
// components
import ProjectsTable from "@/components/Projects/ProjectsTable";
import ProjectsForm from "@/components/Projects/ProjectsForm";

export default {
  name: "Projects",
  components: {
    ProjectsTable,
    ProjectsForm,
  },
  data() {
    return {
      showTableLoader: false,
      tableData: null,
      filterSearch: "",
      expandMenu: false,
      dropdownsLoading: true,
      clients: [],
      statuses: [],
      types: [],
      checklistItems: [],
      priorities: [],
      staffList: [],
      DRIs: [],
      filterClient: "",
      filterPM: "",
      filterEstStartDate: [],
      filterStatus: "",
      filterPriority: "",
      poModal: false,
      itemToEdit: {},
    };
  },
  computed: {
    filterEstStartDateRange: {
      get: function () {
        return this.filterEstStartDate
          .map((date) => this.formatPickerDate(date))
          .join(" - ");
      },
      set: function (newVal) {
        return (
          this.formatPickerDate(newVal)
            ? [...this.formatPickerDate(newVal)]
            : []
        ).join(" - ");
      },
    },
    filterSearchBadge() {
      return this.filterSearch?.length <= 6
        ? this.filterSearch
        : `${this.filterSearch?.slice(0, 6)}...`;
    },
  },
  mixins: [customBreakpoint, timeUnits],
  watch: {
    filterSearch(newValue) {
      const currentQuery = this.$route.query;
      const newQuery =
        newValue !== ""
          ? {
              ...currentQuery,
              Search: newValue,
            }
          : _.omit(currentQuery, ["Search"]);

      this.$router
        .push({ path: `/${ROUTES.projects}`, query: newQuery })
        .catch(() => {}); // Avoided redundant navigation error handler
    },
  },
  mounted() {
    this.handleQuery();
    this.getTableData();
    this.handleDropdowns();
  },
  methods: {
    ...mapActions("projects", ["aSetRequestTypes", "aSetCustomerPriceList", "aSetGeosList"]),
    fullName(item) {
      return `${item?.user?.firstName} ${item?.user?.lastName}`;
    },
    handleDropdowns() {
      let requestTypes = [];
      let customerPriceList = [];
      let geosList = [];
      Promise.all([
        getClients(),
        getEnum("ProjectStatus"),
        getEnum("ProjectType"),
        getEnum("Checklist"),
        getEnum("Priority"),
        getAllStaff(),
        getSpecificEnum('request-types'),
        getCustomerPriceList(),
        getSpecificEnum("geos"),
      ])
        .then((resp) => {
          [
            this.clients,
            this.statuses,
            this.types,
            this.checklistItems,
            this.priorities,
            this.staffList,
            requestTypes,
            customerPriceList,
            geosList,
          ] = resp;
          this.DRIs =
            resp[5].filter(
              (el) =>
                el.user.userRole === "ADMIN" || el.user.userRole === "MANAGER"
            ) ?? [];
          this.aSetCustomerPriceList(customerPriceList);
          this.aSetRequestTypes(requestTypes.data);
          this.aSetGeosList(geosList.data);
          this.dropdownsLoading = false;
        })
        .catch((err) => {
          console.error(err);
          this.dropdownsLoading = false;
        });
    },
    getTableData() {
      this.showTableLoader = true;
      getAllProjects().then((data) => {
        this.tableData = data?.status === 500 ? [] : data;
        this.showTableLoader = false;
      });
    },
    handleFilterQuery(param, data) {
      const currentQuery = this.$route.query;

      const newQuery = {
        ...currentQuery,
        [param]: data,
      };
      this.$router
        .push({ path: `/${ROUTES.projects}`, query: newQuery })
        .catch(() => {}); // Avoided redundant navigation error handler

      this[`filter${param}`] = data;
    },
    clearFilters(dateOnly) {
      let query = this.$route.query;

      if (dateOnly) {
        query = _.omit(query, ["EstStartDate"]);
      } else {
        this.filterClient =
          this.filterPM =
          this.filterStatus =
          this.filterPriority =
          this.filterSearch =
            "";
      }

      this.filterEstStartDate = [];

      this.$router.push({ path: `/${ROUTES.projects}` }).catch(() => {}); // Avoided redundant navigation error handler
    },
    handleQuery() {
      const currentQuery = this.$route.query;

      if (!_.isEmpty(currentQuery))
        Object.keys(currentQuery).forEach((param) => {
          if (param === "EstStartDate") {
            this[`filter${param}`] =
              typeof currentQuery[param] === "string"
                ? [currentQuery[param]]
                : currentQuery[param];
          } else {
            this[`filter${param}`] = currentQuery[param];
          }
        });
    },
    handleForm(tableRow) {
      this.itemToEdit = { ...tableRow };
      this.poModal = true;
    },
    handleRefresh() {
      this.poModal = false;
      this.getTableData();
    },
  },
};
</script>

<style lang="scss" scoped>
.project {
  max-width: 1500px;
  margin: 0 auto;

  &__main-selector {
    width: 250px;
    &.laptop {
      width: 200px;
    }
  }

  &__secondary-selector {
    width: 180px;
    &.laptop {
      width: 130px;
    }
  }

  &__date-selector {
    width: 220px;
  }

  &__search-selector {
    width: 340px;
  }
}

::v-deep {
  .selector.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
    > .v-input__control
    > .v-input__slot {
    padding-right: 0;
  }
  .selector.v-text-field.v-text-field--solo .v-input__append-inner {
    padding-left: 0;
  }
  .selector.v-autocomplete.v-select.v-input--is-focused input {
    min-width: 0;
  }
  .selector.v-text-field.v-input--dense:not(.v-text-field--outlined) input {
    padding: 2px 0;
  }
  .filter-checkbox .v-input--selection-controls__input {
    margin-right: 0;
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}

@import "~vuetify/src/styles/settings/_variables";

@media #{map-get($display-breakpoints, 'lg-and-down')} {
  .v-application .overline {
    font-size: 0.6rem !important;
  }
}
</style>
